import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import MonthNavigator from '../components/MonthNavigator'

function currentMonth() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function daysInMonth(month) {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m, 0).getDate()
}

function clampScale(value) {
  return Math.min(2.5, Math.max(0.75, value))
}

export default function RecordsPage() {
  const navigate = useNavigate()
  const shellRef = useRef(null)
  const viewportRef = useRef(null)
  const gestureRef = useRef(null)
  const activePointersRef = useRef(new Map())
  const lastTapRef = useRef(null)
  const [month, setMonth] = useState(currentMonth())
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [viewerFullscreen, setViewerFullscreen] = useState(false)
  const [tableScale, setTableScale] = useState(1)
  const [tableOffset, setTableOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    api(`/records?month=${month}`)
      .then((data) => alive && setRecords(data))
      .catch((e) => alive && setError(e.message))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [month])

  useEffect(() => {
    const handleFullscreenChange = () => {
      const active =
        document.fullscreenElement === shellRef.current ||
        document.webkitFullscreenElement === shellRef.current
      setViewerFullscreen(active)
      if (!active && screen.orientation?.unlock) {
        try {
          screen.orientation.unlock()
        } catch {
          // ignore
        }
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
    }
  }, [])

  const toggleFullscreen = async () => {
    const shell = shellRef.current
    if (!shell) return

    try {
      if (viewerFullscreen) {
        const exitFullscreen = document.exitFullscreen || document.webkitExitFullscreen
        if (exitFullscreen) {
          await exitFullscreen.call(document)
        } else {
          setViewerFullscreen(false)
        }
        return
      }

      setViewerFullscreen(true)
      const requestFullscreen =
        shell.requestFullscreen || shell.webkitRequestFullscreen || shell.msRequestFullscreen
      if (requestFullscreen) {
        await requestFullscreen.call(shell)
      }
      if (screen.orientation?.lock) {
        try {
          await screen.orientation.lock('landscape')
        } catch {
          // ignore orientation lock failures on unsupported browsers
        }
      }
    } catch (e) {
      setError(e.message || '無法切換全螢幕')
    }
  }

  const getPoint = (event) => {
    const rect = viewportRef.current?.getBoundingClientRect()
    if (!rect) return { x: 0, y: 0 }
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)

  const handlePointerDown = (event) => {
    if (!viewportRef.current) return
    viewportRef.current.setPointerCapture?.(event.pointerId)

    const point = getPoint(event)
    const pointers = new Map(activePointersRef.current)
    pointers.set(event.pointerId, { point, pointerType: event.pointerType })
    activePointersRef.current = pointers

    if (pointers.size === 1) {
      gestureRef.current = {
        mode: 'press',
        pointerId: event.pointerId,
        startPoint: point,
        startOffset: tableOffset,
        startTime: Date.now(),
        pointerType: event.pointerType,
        moved: false,
      }
      return
    }

    if (pointers.size === 2) {
      const [p1, p2] = [...pointers.values()].map((v) => v.point)
      const center = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }
      gestureRef.current = {
        mode: 'pinch',
        startScale: tableScale,
        startOffset: tableOffset,
        startDistance: distance(p1, p2),
        contentPoint: {
          x: (center.x - tableOffset.x) / tableScale,
          y: (center.y - tableOffset.y) / tableScale,
        },
      }
      setIsDragging(false)
    }
  }

  const handlePointerMove = (event) => {
    const activeGesture = gestureRef.current
    if (!activeGesture) return

    const point = getPoint(event)
    const pointers = new Map(activePointersRef.current)
    const entry = pointers.get(event.pointerId)
    if (!entry) return
    pointers.set(event.pointerId, { ...entry, point })
    activePointersRef.current = pointers

    if (activeGesture.mode === 'press' && activeGesture.pointerId === event.pointerId) {
      const dx = point.x - activeGesture.startPoint.x
      const dy = point.y - activeGesture.startPoint.y
      if (!activeGesture.moved && Math.hypot(dx, dy) > 6) {
        gestureRef.current = { ...activeGesture, mode: 'drag', moved: true }
        setIsDragging(true)
      }
    }

    if (gestureRef.current?.mode === 'drag' && gestureRef.current.pointerId === event.pointerId) {
      const dragGesture = gestureRef.current
      const dx = point.x - dragGesture.startPoint.x
      const dy = point.y - dragGesture.startPoint.y
      setTableOffset({
        x: dragGesture.startOffset.x + dx,
        y: dragGesture.startOffset.y + dy,
      })
      return
    }

    if (gestureRef.current?.mode === 'pinch' && activePointersRef.current.size >= 2) {
      const [p1, p2] = [...activePointersRef.current.values()].map((v) => v.point)
      const center = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 }
      const nextScale = clampScale(
        (gestureRef.current.startScale * distance(p1, p2)) / gestureRef.current.startDistance
      )
      setTableScale(nextScale)
      setTableOffset({
        x: center.x - gestureRef.current.contentPoint.x * nextScale,
        y: center.y - gestureRef.current.contentPoint.y * nextScale,
      })
    }
  }

  const handlePointerUp = (event) => {
    const activeGesture = gestureRef.current
    const pointerInfo = activePointersRef.current.get(event.pointerId)
    const pointers = new Map(activePointersRef.current)
    pointers.delete(event.pointerId)
    activePointersRef.current = pointers

    if (activeGesture?.mode === 'press' && activeGesture.pointerId === event.pointerId) {
      const point = getPoint(event)
      const elapsed = Date.now() - activeGesture.startTime
      const moved = Math.hypot(point.x - activeGesture.startPoint.x, point.y - activeGesture.startPoint.y)

      if (pointerInfo?.pointerType !== 'mouse' && elapsed < 300 && moved < 10) {
        const lastTap = lastTapRef.current
        const isDoubleTap =
          lastTap &&
          Date.now() - lastTap.time < 320 &&
          Math.hypot(point.x - lastTap.point.x, point.y - lastTap.point.y) < 24

        if (isDoubleTap) {
          lastTapRef.current = null
          toggleFullscreen()
        } else {
          lastTapRef.current = { time: Date.now(), point }
        }
      }

      gestureRef.current = null
      setIsDragging(false)
      return
    }

    if (activeGesture?.mode === 'drag' && activeGesture.pointerId === event.pointerId) {
      gestureRef.current = null
      setIsDragging(false)
      return
    }

    if (activeGesture?.mode === 'pinch') {
      if (pointers.size === 1) {
        const [remainingId, remaining] = [...pointers.entries()][0]
        gestureRef.current = {
          mode: 'press',
          pointerId: remainingId,
          startPoint: remaining.point,
          startOffset: tableOffset,
          startTime: Date.now(),
          pointerType: remaining.pointerType,
          moved: false,
        }
      } else if (pointers.size === 0) {
        gestureRef.current = null
        setIsDragging(false)
      }
    }
  }

  const handleDoubleClick = (event) => {
    event.preventDefault()
    toggleFullscreen()
  }

  const byDate = {}
  records.forEach((r) => {
    byDate[r.date] = r
  })
  const days = daysInMonth(month)

  const tableRows = Array.from({ length: days }, (_, i) => i + 1).map((d) => {
    const date = `${month}-${String(d).padStart(2, '0')}`
    return { date, ...(byDate[date] ?? {}) }
  })

  const printTitle = (() => {
    const [y, m] = month.split('-').map(Number)
    return `${y - 1911} 年 ${m} 月門診追蹤摘要`
  })()

  function countDialysisSessions(rec) {
    const vals = [
      rec.ultrafiltration_1,
      rec.ultrafiltration_2,
      rec.ultrafiltration_3,
      rec.ultrafiltration_4,
      rec.ultrafiltration_5,
    ]
    return vals.filter((v) => v != null).length
  }

  function washSummaryCells(rec) {
    const count = countDialysisSessions(rec)
    if (count === 2) {
      return [
        rec.ultrafiltration_1 != null ? rec.ultrafiltration_1 + 2000 : null,
        rec.ultrafiltration_2 ?? null,
        null,
      ]
    }
    if (count > 2) {
      const pair = (a, b) => (a == null && b == null ? '' : `${a ?? ''} / ${b ?? ''}`)
      return [
        pair(rec.ultrafiltration_1, rec.ultrafiltration_2),
        pair(rec.ultrafiltration_3, rec.ultrafiltration_4),
        rec.ultrafiltration_5 ?? '',
      ]
    }
    return [null, null, null]
  }

  function calcDayTotal(rec) {
    return rec.total_ultrafiltration ?? null
  }

  function exportPdf() {
    const originalTitle = document.title
    document.title = `${month.replace('-', '')}.pdf`
    const restore = () => {
      document.title = originalTitle
      window.removeEventListener('afterprint', restore)
    }
    window.addEventListener('afterprint', restore)
    window.print()
  }

  return (
    <div id="print-page">
      <h1 className="mb-3 text-3xl font-bold text-white">📊 每日健康記錄</h1>
      <MonthNavigator value={month} onChange={setMonth} />
      {error && <p className="mb-2 text-center text-xl font-bold text-red-300">⚠️ {error}</p>}
      {loading && <p className="text-center text-2xl text-gray-300">載入中…</p>}
      {!loading && (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
            const date = `${month}-${String(d).padStart(2, '0')}`
            const rec = byDate[date]
            return (
              <button
                key={date}
                type="button"
                onClick={() => navigate(`/records/${date}`)}
                className={`flex flex-col items-start rounded-2xl border-4 p-3 text-left shadow-lg transition ${
                  rec
                    ? 'border-white bg-safe text-white'
                    : 'border-navy-700 bg-navy-800 text-gray-200'
                }`}
              >
                <span className="text-2xl font-bold">{d} 日</span>
                {rec ? (
                  <>
                    <span className="mt-1 text-lg leading-tight">
                      🩸 {rec.blood_pressure_sys || '—'}/{rec.blood_pressure_dia || '—'}
                    </span>
                    <span className="text-lg leading-tight">
                      ⚖️ {rec.weight ?? '—'} 公斤
                    </span>
                    <span className="text-lg leading-tight">
                      💧 洗水 {rec.total_ultrafiltration ?? 0}
                    </span>
                    <span className="text-lg leading-tight">
                      🌅 起床血糖 {rec.morning_blood_sugar ?? '—'}
                    </span>
                  </>
                ) : (
                  <span className="mt-1 text-lg text-gray-400">還沒記錄</span>
                )}
              </button>
            )
          })}
        </div>
      )}

      <div
        id="print-shell"
        ref={shellRef}
        className={
          viewerFullscreen
            ? 'fixed inset-0 z-50 flex flex-col bg-navy-950 p-3'
            : 'mt-5 rounded-2xl border-4 border-white bg-white/10 p-3'
        }
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="text-2xl font-bold text-white">📄 Excel 表格</h2>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-navy-700 px-3 py-1 text-lg font-bold text-white">
              {tableRows.length} 天
            </span>
            <button
              type="button"
              onClick={exportPdf}
              className="rounded-full bg-safe px-4 py-1 text-lg font-bold text-white hover:bg-green-600"
            >
              🖨️ 輸出 PDF
            </button>
          </div>
        </div>
        <div
          id="print-viewport"
          ref={viewportRef}
          className={`select-none overflow-hidden rounded-xl border-2 border-white bg-white ${
            viewerFullscreen ? 'flex-1' : 'max-h-[65vh]'
          } ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDoubleClick={handleDoubleClick}
          style={{ touchAction: 'none' }}
        >
          <div
            id="print-table"
            className="inline-block min-w-full"
            style={{
              transform: `translate(${tableOffset.x}px, ${tableOffset.y}px) scale(${tableScale})`,
              transformOrigin: 'top left',
              willChange: 'transform',
            }}
          >
            <div id="print-title">{printTitle}</div>
            <table className="w-full border-collapse text-left text-lg">
              <thead className="sticky top-0 bg-navy-700 text-white">
                <tr>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">日期</th>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">血壓</th>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">心跳</th>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">體溫</th>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">體重</th>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">APD0周期</th>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">APD總脫水量</th>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">CAPD總脫水量</th>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">全天總脫水量</th>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">睡前血糖</th>
                  <th className="whitespace-nowrap border-r border-white px-3 py-2">胰島素(速效/長效)</th>
                  <th className="whitespace-nowrap px-3 py-2">起床血糖</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.length ? (
                  tableRows.map((rec, index) => {
                    const [washCell1, washCell2, washCell3] = washSummaryCells(rec)
                    return (
                    <tr key={rec.date} className={index % 2 === 0 ? 'bg-sky-50' : 'bg-white'}>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 font-bold text-navy-900">
                        {`${Number(rec.date.slice(5, 7))}月${rec.date.slice(8, 10)}日`}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 text-navy-900">
                        {rec.blood_pressure_sys == null && rec.blood_pressure_dia == null ? '' : `${rec.blood_pressure_sys ?? ''}/${rec.blood_pressure_dia ?? ''}`}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 text-navy-900">
                        {rec.heart_rate ?? ''}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 text-navy-900">
                        {rec.body_temperature ?? ''}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 text-navy-900">
                        {rec.weight ?? ''}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 text-navy-900">
                        {washCell1 ?? ''}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 text-navy-900">
                        {washCell2 ?? ''}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 text-navy-900">
                        {washCell3 ?? ''}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 text-navy-900">
                        {calcDayTotal(rec) ?? ''}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 text-navy-900">
                        {rec.bedtime_blood_sugar ?? ''}
                      </td>
                      <td className="whitespace-nowrap border-r border-gray-300 px-3 py-2 text-navy-900">
                        {rec.insulin_rapid == null && rec.insulin_long == null ? '' : `${rec.insulin_rapid ?? ''} / ${rec.insulin_long ?? ''}`}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-navy-900">
                        {rec.morning_blood_sugar ?? ''}
                      </td>
                    </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={11} className="px-3 py-6 text-center text-xl text-gray-500">
                      這個月還沒有記錄，先點日期輸入資料
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <p className="mt-2 text-center text-lg text-gray-200">單指平移，雙指縮放，雙點或雙擊全螢幕。</p>
      </div>

      <p className="mt-4 text-center text-xl text-gray-300">點選任一日期，開始記錄</p>
    </div>
  )
}
